// frontend/src/lib/riskQuestionSet.js
import { AUDIT_QUESTIONS } from "../data/questions";

/**
 * Normalize jurisdictions to stable uppercase tokens.
 * Supports:
 * - config.jurisdictions (array)
 * - config.jurisdiction (string)
 *
 * IMPORTANT PRODUCT RULE:
 * - Detailed Assessment always uses the full 60-question bank (no filtering).
 * - Jurisdictions are still normalized here for:
 *    - scope display
 *    - regime overlays/weighting
 *    - downstream risk exposure modeling
 */
export function normalizeJurisdictions(config) {
  const raw = config?.jurisdictions ?? config?.jurisdiction ?? [];
  const arr = Array.isArray(raw) ? raw : [raw];

  const normalized = arr
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .map((x) => x.toUpperCase());

  // Option B (safer): bridge US umbrella tags
  // If user selected US_FED and/or US_STATE, also include "US"
  const set = new Set(normalized);
  if (set.has("US_FED") || set.has("US_STATE")) set.add("US");

  return Array.from(set);
}

/**
 * Question applicability:
 * We keep this helper for future overlays, but we do NOT use it to filter
 * the Detailed Assessment question list (product rule: always 60).
 */
export function questionAppliesToSelection(question, selectedJurisdictions) {
  const qJ = question?.jurisdictions;
  if (!qJ) return true;

  const qArr = Array.isArray(qJ) ? qJ : [qJ];
  const normalized = qArr
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .map((x) => x.toUpperCase());

  if (!normalized.length) return true;
  if (normalized.includes("ALL")) return true;

  // Also bridge "US" umbrella here, just in case
  const sel = new Set(Array.isArray(selectedJurisdictions) ? selectedJurisdictions : []);
  if (sel.has("US_FED") || sel.has("US_STATE")) sel.add("US");

  if (!sel.size) return true;
  return normalized.some((j) => sel.has(j));
}

/**
 * Build the active question list (stable order; dedupe by id).
 * PRODUCT RULE: Always return the full master bank.
 */
export function getRelevantQuestions(config) {
  const selected = normalizeJurisdictions(config); // used for metadata only
  const seen = new Set();
  const out = [];

  const bank = Array.isArray(AUDIT_QUESTIONS) ? AUDIT_QUESTIONS : [];

  bank.forEach((q) => {
    if (!q || q.id === undefined || q.id === null) return;

    const key = String(q.id);
    if (seen.has(key)) return;
    seen.add(key);

    // attach lightweight applicability metadata for optional UI overlays
    out.push({
      ...q,
      __applies_to_selected_scope: questionAppliesToSelection(q, selected),
    });
  });

  // stable ordering by numeric id
  out.sort((a, b) => Number(a?.id) - Number(b?.id));

  return out;
}
